import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { compact } from 'lodash-es';
import { useHistory, useLocation } from 'react-router';
import { Page, Tabs, Typography, Divider, PageContentHeader, Callout, Button, useOverlayContext, ConfirmationModal } from 'seagull';
import { EmailSent } from 'seagull/icons';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useEmailAuthCompletion } from '~/hooks/useEmailAuthCompletion';
import { useRestfulWrapper } from '~/hooks/useRestfulWrapper';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { EmailOnBehalfSettingsModel } from '~/models/EmailOnBehalfSettingsModel';
import { selectors } from '~/store/state-selectors';
import { helpArticles } from '~/utils/helpArticles';
import pluralize from '~/utils/pluralize';
import styles from './NotificationSettings.module.scss';
import { Tab, Table, deriveLegacyIsActive, isDays, minutesToDays, getRecipientTypesText, reminderColumnDefsMap, getReminderHeaderItems, notificationColumnDefs, getNotificationHeaderItems, getEmailProviderDetails, notificationSettingsTextsPath } from './NotificationSettings.utils';
import { NotificationSettingsTable } from './NotificationSettingsTable';
import { SendEmailOnBehalfModal } from './SendEmailOnBehalfModal';
const NotificationSettings = ()=>{
    const { t: texts } = useLocaleKeys(notificationSettingsTextsPath);
    const { t: commonTexts } = useLocaleKeys('common');
    const { api } = useStoreActions();
    const history = useHistory();
    const businessId = useStore(selectors.user.businessId);
    const location = useLocation();
    const [selectedTab, setSelectedTab] = useState(Tab.Vendor);
    const overlayContext = useOverlayContext();
    const [emailOnBehalf, setEmailOnBehalf] = useState(EmailOnBehalfSettingsModel.makeDisconnected());
    const { startLoader, stopLoader, showErrorToast } = useOverlayContext();
    const { logBI } = useStoreActions();
    const fetchData = async ()=>{
        if (!businessId) {
            history.push(routes.home);
            return {};
        }
        return api.notification.getNotificationSettings(businessId).then((data)=>{
            setEmailOnBehalf(data.sendOnBehalfOf);
            return data;
        });
    };
    const { refresh, renderRestfulState } = useRestfulWrapper({
        fetch: fetchData
    });
    useEffect(()=>{
        var _location_state;
        const selectedTab = (_location_state = location.state) === null || _location_state === void 0 ? void 0 : _location_state.selectedTab;
        if (selectedTab) {
            setSelectedTab(selectedTab);
        }
    }, [
        location.state
    ]);
    useEmailAuthCompletion({
        businessId,
        onSuccess: async ()=>{
            await refresh();
            setSelectedTab(Tab.Client);
        },
        onError: (errorMessage, toastId)=>{
            showErrorToast(errorMessage, toastId);
            setSelectedTab(Tab.Client);
        },
        onAccessDenied: ()=>{
            showErrorToast(commonTexts.toasts.failedToConnectYourEmailAccount(), 'integrations-access-denied');
            setSelectedTab(Tab.Client);
        }
    });
    const connectEmailOnBehalf = async ()=>{
        logBI(18500, {
            vendorId: businessId
        });
        overlayContext.showModal({
            biName: 'send_email_on_behalf_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(SendEmailOnBehalfModal, {
                    dismissModal: dismissModal
                });
            }
        });
    };
    const disconnectEmailOnBehalf = async ()=>{
        const provider = emailOnBehalf.provider();
        if (!provider) return;
        const providerDetails = getEmailProviderDetails(provider);
        overlayContext.showModal({
            biName: 'notification_settings_are_you_sure_confirm',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'notification_settings_confirm'
                        },
                        secondaryButton: {
                            name: 'notification_settings_cancel'
                        }
                    },
                    skin: "danger",
                    title: texts.areYouSure(),
                    textAlignCenter: false,
                    content: /*#__PURE__*/ _jsxs(Typography, {
                        children: [
                            texts.disconnectConfirmation(),
                            ' ',
                            /*#__PURE__*/ _jsx(Typography, {
                                as: "span",
                                weight: "bold",
                                children: "system@notifications.sayanchor.com"
                            })
                        ]
                    }),
                    primaryButtonLabel: commonTexts.buttons.disconnect(),
                    secondaryButtonLabel: commonTexts.buttons.cancel(),
                    secondaryButtonTestId: "disconnect-modal-cancel-button",
                    primaryButtonTestId: "disconnect-modal-primary-button",
                    onCloseButtonClick: dismissModal,
                    onSecondaryButtonClick: dismissModal,
                    onPrimaryButtonClick: async ()=>{
                        if (businessId) {
                            startLoader();
                            const { success } = await handleRequest(api.notification.emailOnBehalf.disconnectEmailOnBehalf(businessId));
                            if (success) {
                                setEmailOnBehalf(EmailOnBehalfSettingsModel.makeDisconnected());
                                dismissModal();
                                logBI(providerDetails.biCodes.disconnect, {
                                    vendorId: businessId
                                });
                            } else {
                                showErrorToast(commonTexts.toasts.failedToDisconnectGmailAccount(), 'disconnect-email-on-behalf-error-toast');
                            }
                            stopLoader();
                        }
                    }
                });
            }
        });
    };
    return /*#__PURE__*/ _jsxs(Page, {
        header: texts.header(),
        subheader: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                texts.setNotifications(),
                /*#__PURE__*/ _jsx(ExternalLinkButton, {
                    href: helpArticles.notificationSettings(),
                    testId: 'learn-more-link',
                    openInNewTab: true,
                    className: styles.learnMoreLink,
                    underline: "none",
                    bi: {
                        name: 'notification_settings_learn_more'
                    },
                    children: texts.learnMore()
                })
            ]
        }),
        testId: "notification-settings",
        children: [
            /*#__PURE__*/ _jsx(PageContentHeader, {
                children: /*#__PURE__*/ _jsx(Tabs, {
                    bi: {
                        name: 'notification_settings_tabs'
                    },
                    value: selectedTab,
                    onChange: (_, value)=>setSelectedTab(value),
                    tabs: [
                        {
                            component: texts.tabs.teamNotifications(),
                            id: Tab.Vendor,
                            value: Tab.Vendor,
                            testId: "vendor-notifications-tab"
                        },
                        {
                            component: texts.tabs.clientNotifications(),
                            id: Tab.Client,
                            value: Tab.Client,
                            testId: "client-notifications-tab"
                        }
                    ]
                })
            }),
            renderRestfulState({
                renderChild: (param)=>{
                    let { data, loading, refresh } = param;
                    return /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            !loading && selectedTab === Tab.Client && /*#__PURE__*/ _jsx(Callout, {
                                icon: /*#__PURE__*/ _jsx(EmailSent, {}),
                                className: styles.mailCallout,
                                testId: 'connect-email-on-behalf-callout',
                                variant: "paragraph1",
                                withAction: emailOnBehalf.isDisconnected() ? /*#__PURE__*/ _jsx(Button, {
                                    bi: {
                                        name: 'connect_email_on_behalf'
                                    },
                                    skin: "primary",
                                    testId: 'connect-email-on-behalf-button',
                                    onClick: connectEmailOnBehalf,
                                    size: "small",
                                    children: texts.sendEmailsUsingMyOwnDomain()
                                }) : /*#__PURE__*/ _jsx(Button, {
                                    bi: {
                                        name: 'disconnect_email_on_behalf'
                                    },
                                    skin: "primary",
                                    testId: 'disconnect-email-on-behalf-button',
                                    onClick: disconnectEmailOnBehalf,
                                    size: "small",
                                    children: texts.disconnect()
                                }),
                                children: /*#__PURE__*/ _jsxs(Typography, {
                                    testId: 'connect-email-on-behalf-text',
                                    children: [
                                        texts.clientNotificationsFromBanner(),
                                        ' ',
                                        /*#__PURE__*/ _jsx(Typography, {
                                            as: "span",
                                            weight: "bold",
                                            children: emailOnBehalf.email()
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                testId: "".concat(Table.Notifications, "-table-title"),
                                variant: "h6",
                                className: styles.tableTitle,
                                children: texts.tableTitles.notifications()
                            }),
                            /*#__PURE__*/ _jsx(NotificationSettingsTable, {
                                data: data,
                                refresh: refresh,
                                tableType: Table.Notifications,
                                columnDefs: notificationColumnDefs,
                                headerItems: getNotificationHeaderItems(texts),
                                selectedTab: selectedTab,
                                getRowItems: (param)=>{
                                    let { notification, text, key } = param;
                                    return [
                                        {
                                            value: /*#__PURE__*/ _jsx(Typography, {
                                                weight: "normal",
                                                children: text
                                            }),
                                            testId: "".concat(Table.Notifications, "-row-").concat(key, "-title")
                                        },
                                        {
                                            value: /*#__PURE__*/ _jsx(Typography, {
                                                children: deriveLegacyIsActive(notification) ? texts.statusActive() : texts.statusInactive()
                                            }),
                                            testId: "".concat(Table.Notifications, "-row-").concat(key, "-status")
                                        },
                                        {
                                            value: getRecipientTypesText({
                                                recipientTypes: notification.recipientTypes,
                                                notificationName: key,
                                                texts
                                            }).map((text)=>/*#__PURE__*/ _jsx(Typography, {
                                                    as: "div",
                                                    className: styles.preserveLineBreak,
                                                    children: text
                                                }, text)),
                                            testId: "".concat(Table.Notifications, "-row-").concat(key, "-notified-team-members")
                                        }
                                    ];
                                }
                            }),
                            /*#__PURE__*/ _jsx(Divider, {}),
                            /*#__PURE__*/ _jsx(Typography, {
                                testId: "".concat(Table.Reminders, "-table-title"),
                                variant: "h6",
                                className: styles.tableTitle,
                                children: texts.tableTitles.reminders()
                            }),
                            selectedTab === Tab.Client && /*#__PURE__*/ _jsx(Typography, {
                                testId: "".concat(Table.Reminders, "-subtitle"),
                                skin: "secondary",
                                variant: "paragraph1",
                                children: texts.reminderIntervalDescription()
                            }),
                            /*#__PURE__*/ _jsx(NotificationSettingsTable, {
                                data: data,
                                refresh: refresh,
                                tableType: Table.Reminders,
                                columnDefs: reminderColumnDefsMap[selectedTab],
                                headerItems: getReminderHeaderItems(selectedTab, texts),
                                selectedTab: selectedTab,
                                getRowItems: (param)=>{
                                    let { notification, text, key } = param;
                                    return compact([
                                        {
                                            value: /*#__PURE__*/ _jsx(Typography, {
                                                weight: "normal",
                                                children: text
                                            }),
                                            testId: "reminders-row-".concat(key, "-title")
                                        },
                                        {
                                            value: /*#__PURE__*/ _jsx(Typography, {
                                                children: deriveLegacyIsActive(notification) ? texts.statusActive() : texts.statusInactive()
                                            }),
                                            testId: "reminders-row-".concat(key, "-status")
                                        },
                                        {
                                            value: notification.recipientTypes.length ? isDays(notification.recurring.minutes) ? pluralize('day', minutesToDays(notification.recurring.minutes), true) : pluralize('minute', notification.recurring.minutes, true) : '',
                                            testId: "reminders-row-".concat(key, "-").concat(selectedTab === Tab.Client ? 'interval' : 'delay')
                                        },
                                        selectedTab === Tab.Client && {
                                            value: notification.recipientTypes.length ? notification.recurring.times : '',
                                            testId: "reminders-row-".concat(key, "-retries")
                                        },
                                        {
                                            value: getRecipientTypesText({
                                                recipientTypes: notification.recipientTypes,
                                                notificationName: key,
                                                texts
                                            }).map((text)=>/*#__PURE__*/ _jsx(Typography, {
                                                    as: "div",
                                                    className: styles.preserveLineBreak,
                                                    children: text
                                                }, text)),
                                            testId: "reminders-row-".concat(key, "-notified-team-members")
                                        }
                                    ]);
                                }
                            })
                        ]
                    });
                }
            })
        ]
    });
};
export default NotificationSettings;
